import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box, ButtonBase, IconButton, MenuItem, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ViewModuleIcon       from '@mui/icons-material/ViewModule';
import ViewListIcon         from '@mui/icons-material/ViewList';
import PaletteOutlinedIcon  from '@mui/icons-material/PaletteOutlined';
import TextFormatIcon       from '@mui/icons-material/TextFormat';
import VerticalSplitIcon    from '@mui/icons-material/VerticalSplit';
import HorizontalSplitIcon  from '@mui/icons-material/HorizontalSplit';
import CloseIcon            from '@mui/icons-material/Close';
import AddIcon              from '@mui/icons-material/Add';
import LocalOfferOutlinedIcon       from '@mui/icons-material/LocalOfferOutlined';
import StraightenIcon       from '@mui/icons-material/Straighten';
import AttachMoneyIcon      from '@mui/icons-material/AttachMoney';
import MenuIcon             from '@mui/icons-material/Menu';
import GridViewIcon         from '@mui/icons-material/GridView';
import { ConfigCard } from '../cards';
import { ChipsBox, ChipToggle, AvailableChip, SubLabel, PdiRow } from '../cards/_PdiHelpers';

// Filter types the storefront supports. Keys mirror the legacy storefront's
// SIDE_FILTER_ORDER tokens (plural form for brands/sizes/colors/seasons;
// the storefront's filter-mobile.component.html keys off these names). The
// per-shop cat1..cat7 entries are appended dynamically using shopFieldLabels
// (the customer's kasvars labels for category_1..7), so each retailer sees
// their own taxonomy names rather than generic "Category 1".
const STATIC_FILTERS = [
    { key: 'price',   i18nKey: 'cfg_groups_filter_price',   fallback: 'Price',   icon: <AttachMoneyIcon         fontSize="small" /> },
    { key: 'brands',  i18nKey: 'cfg_groups_filter_brands',  fallback: 'Brand',   icon: <LocalOfferOutlinedIcon  fontSize="small" /> },
    { key: 'sizes',   i18nKey: 'cfg_groups_filter_sizes',   fallback: 'Sizes',   icon: <GridViewIcon            fontSize="small" /> },
    { key: 'colors',  i18nKey: 'cfg_groups_filter_colors',  fallback: 'Colors',  icon: <PaletteOutlinedIcon     fontSize="small" /> },
    { key: 'seasons', i18nKey: 'cfg_groups_filter_seasons', fallback: 'Season',  icon: <MenuIcon                fontSize="small" /> },
];
// Build the full filter set for a given shop. Cat entries get their label
// from kasvars (shopFieldLabels.category_N); fall back to a generic name so
// the chip is still visible if a customer hasn't filled in that category.
// One shared element — elements are immutable, no need for 12 fresh ones.
const CATEGORY_MENU_ICON = <MenuIcon fontSize="small" />;

const buildAllFilters = (shopFieldLabels = {}) => [
    ...STATIC_FILTERS,
    ...[1, 2, 3, 4, 5, 6, 7].map(n => ({
        key:      `cat${n}`,
        label:    shopFieldLabels[`category_${n}`] || `Category ${n}`,
        icon:     CATEGORY_MENU_ICON,
    })),
];
// Matches mig017_countries.php install default for SIDE_FILTER_ORDER.
const DEFAULT_ACTIVE_FILTERS = ['cat1', 'cat2', 'cat3', 'cat4', 'cat5', 'brands', 'sizes', 'colors'];

// Mobile hierarchy is FILTER_TREE_LEVELS — a CSV of category levels only
// (mig106_20201015.php seeds it from SIDE_FILTER_ORDER's cat* tokens).
const buildAllHierarchy = (shopFieldLabels = {}) =>
    [1, 2, 3, 4, 5, 6, 7].map(n => ({
        key:   `cat${n}`,
        label: shopFieldLabels[`category_${n}`] || `Category ${n}`,
    }));
const DEFAULT_HIERARCHY = ['cat1', 'cat2'];

// Sort keys mirror the storefront's `orderByOptions` map (explore.component.ts).
// `default` is the recommended sort (brand_product). EXPLORE_SORT_ORDER's
// install default (mig023) is 'recent,sold,price,discount'.
const ALL_SORTS = [
    { key: 'default',  i18nKey: 'cfg_groups_sort_default',  fallback: 'Recommended (default)' },
    { key: 'recent',   i18nKey: 'cfg_groups_sort_recent',   fallback: 'Most recent' },
    { key: 'sold',     i18nKey: 'cfg_groups_sort_sold',     fallback: 'Best selling' },
    { key: 'stock',    i18nKey: 'cfg_groups_sort_stock',    fallback: 'Most stock' },
    { key: 'price',    i18nKey: 'cfg_groups_sort_price',    fallback: 'Price' },
    { key: 'discount', i18nKey: 'cfg_groups_sort_discount', fallback: 'Discount' },
];
const DEFAULT_SORTS = ['recent', 'sold', 'price', 'discount'];

// Labels resolve through tr() at render time; English text = fallback.
const SORT_RULE_FIELDS = [
    { v: 'price',         i18nKey: 'cfg_groups_rule_price',    l: 'Price' },
    { v: 'genderPriority',i18nKey: 'cfg_groups_rule_gender',   l: 'Gender priority' },
    { v: 'typePriority',  i18nKey: 'cfg_groups_rule_type',     l: 'Type priority' },
    { v: 'id',            i18nKey: 'cfg_groups_rule_id',       l: 'ID' },
    { v: 'createdAt',     i18nKey: 'cfg_groups_rule_created',  l: 'Creation date' },
    { v: 'discount',      i18nKey: 'cfg_groups_rule_discount', l: 'Discount' },
    { v: 'stock',         i18nKey: 'cfg_groups_rule_stock',    l: 'Stock' },
];

// Exported so the parent can seed its state from the same defaults the
// uncontrolled modal would use, and so a `controlled` parent has something
// sensible to fall back to before its backend load resolves.
export const DEFAULT_GROUPS_FILTER_CONFIG = {
    layout: {
        wherefilters:    'sidebar',  // 'topbar' | 'sidebar'
        sizeStyle:       'grid',     // 'grid' | 'list'
        colorStyle:      'swatches', // 'swatches' | 'names'  — frontend-only for now
        splitWidthLength:false,
        showProductCount:true,
        hoverStyle:      'bold',     // 'bold' | 'underline' | 'color'
    },
    activeFilters: [...DEFAULT_ACTIVE_FILTERS],
    // ORDER_SIDE_FILTER_BY — how the values inside the filters are sorted.
    sortFiltersBy: 'NAME',   // NAME | NAME_BUT_MATCH_HEADER | MOST_ITEMS
    // Per-filter defaults — keys match SIDE_FILTER_ORDER tokens. Anything
    // not listed (e.g. cat1..cat7, colors, seasons) lazy-falls back to
    // { itemsBeforeMore: 8, expandedByDefault: true } when first activated.
    filterSettings: {
        brands: { itemsBeforeMore: 25, expandedByDefault: true  },
        sizes:  { itemsBeforeMore: 8,  expandedByDefault: true  },
        price:  { itemsBeforeMore: '', expandedByDefault: true  },
    },
    mobileHierarchy: [...DEFAULT_HIERARCHY],
    sortOptions:    [...DEFAULT_SORTS],
    defaultSortRule: [
        { id: 'r1', field: 'price',          direction: 'asc'  },
        { id: 'r2', field: 'genderPriority', direction: 'asc'  },
        { id: 'r3', field: 'typePriority',   direction: 'asc'  },
        { id: 'r4', field: 'id',             direction: 'desc' },
    ],
};

const GroupsFilterModal = ({ value, onChange, shopFieldLabels = {}, filterStyle = '', theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    // Cat labels come from the customer's kasvars (Code1Oms..7Oms), passed in
    // by WebshopConfigScreen. Memoised (F12): a fresh array per render gave
    // the whole DnD tree new identities on every keystroke in the modal.
    const ALL_FILTERS   = useMemo(() => buildAllFilters(shopFieldLabels), [shopFieldLabels]);
    const ALL_HIERARCHY = useMemo(() => buildAllHierarchy(shopFieldLabels), [shopFieldLabels]);
    // Helper for code that previously called tr(meta.i18nKey, meta.fallback):
    // dynamic entries set `label` directly (no translation), static ones
    // still use i18nKey/fallback. This keeps both paths working through one
    // lookup function.
    const labelOf = (meta) => meta?.label ?? tr(meta?.i18nKey, meta?.fallback);

    // Controlled vs uncontrolled — same pattern as ProductCardModal. When
    // value + onChange are both passed, every edit bubbles up; otherwise
    // we keep state internally so the modal stays usable in isolation.
    const [localConfig, setLocalConfig] = useState(DEFAULT_GROUPS_FILTER_CONFIG);
    const controlled = value !== undefined && typeof onChange === 'function';
    const config    = controlled ? value      : localConfig;
    const setConfig = controlled
        ? (updater) => onChange(typeof updater === 'function' ? updater(config) : updater)
        : setLocalConfig;

    // Each section gets a small setter helper.
    const setLayout         = (patch) => setConfig(p => ({ ...p, layout: { ...p.layout, ...patch } }));
    const setFilterSetting  = (k, patch) => setConfig(p => ({ ...p, filterSettings: { ...p.filterSettings, [k]: { ...(p.filterSettings[k] || {}), ...patch } } }));
    const setActiveFilters  = (list) => setConfig(p => ({ ...p, activeFilters: list }));
    const setMobileHierarchy = (list) => setConfig(p => ({ ...p, mobileHierarchy: list }));
    const setSortOptions    = (list) => setConfig(p => ({ ...p, sortOptions: list }));
    const setSortRules      = (list) => setConfig(p => ({ ...p, defaultSortRule: list }));

    return (
        // Wide modal → flow the cards into two balanced (masonry) columns on
        // large screens so the canvas is actually used; single column otherwise.
        <Box sx={{ columnGap: 2, columns: { xs: '1', lg: '2' }, '& > *': { breakInside: 'avoid', mb: 2 } }}>
            {/* Filter layout */}
            <ConfigCard
                anchor="cfg-card-groups-layout"
                title={tr('cfg_groups_layout_title', 'Filter layout')}
                description={tr('cfg_groups_layout_desc', 'Where filters appear on the explore page.')}
                theme={theme}
            >
                <PdiRow
                    label={tr('cfg_groups_where', 'Where filters appear')}
                    help={tr('cfg_groups_where_help', 'A left sidebar is always shown. This setting decides whether the filters live in that sidebar, or as dropdowns in a top bar above the products.')}
                    align="top"
                    control={
                        <TilePair
                            value={config.layout.wherefilters}
                            options={[
                                { v: 'topbar',  label: tr('cfg_groups_where_topbar',  'In a top bar'),       icon: <HorizontalSplitIcon /> },
                                { v: 'sidebar', label: tr('cfg_groups_where_sidebar', 'In the left sidebar'), icon: <VerticalSplitIcon />   },
                            ]}
                            onChange={(v) => setLayout({ wherefilters: v })}
                        />
                    }
                />
                <PdiRow
                    label={tr('cfg_groups_size_style', 'Size filter style')}
                    help={tr('cfg_groups_size_style_help', 'How the size options are displayed in the filter')}
                    align="top"
                    control={
                        <TilePair
                            value={config.layout.sizeStyle}
                            options={[
                                { v: 'grid', label: tr('cfg_groups_grid', 'Grid'), icon: <ViewModuleIcon /> },
                                { v: 'list', label: tr('cfg_groups_list', 'List'), icon: <ViewListIcon />   },
                            ]}
                            onChange={(v) => setLayout({ sizeStyle: v })}
                        />
                    }
                />
                <PdiRow
                    label={tr('cfg_groups_color_style', 'Color filter style')}
                    help={tr('cfg_groups_color_style_help', 'How the color options are displayed in the filter')}
                    align="top"
                    control={
                        <TilePair
                            value={config.layout.colorStyle}
                            options={[
                                { v: 'swatches', label: tr('cfg_groups_swatches', 'Color swatches'), icon: <PaletteOutlinedIcon /> },
                                { v: 'names',    label: tr('cfg_groups_names',    'Color names'),    icon: <TextFormatIcon />      },
                            ]}
                            onChange={(v) => setLayout({ colorStyle: v })}
                        />
                    }
                />
                <PdiRow
                    label={tr('cfg_groups_split_size', 'Split size filter into width and length')}
                    help={tr('cfg_groups_split_size_help', 'Show two separate filters (e.g. for jeans where size is W32 / L34) instead of one combined size filter')}
                    control={<Switch color="success" size="small" checked={!!config.layout.splitWidthLength} onChange={(e) => setLayout({ splitWidthLength: e.target.checked })} />}
                />
                <PdiRow
                    label={tr('cfg_groups_show_count', 'Show product count next to filter values')}
                    help={tr('cfg_groups_show_count_help', 'Each filter value shows how many products match it (e.g. "Red (12)")')}
                    control={<Switch color="success" size="small" checked={!!config.layout.showProductCount} onChange={(e) => setLayout({ showProductCount: e.target.checked })} />}
                />
                <PdiRow
                    label={tr('cfg_groups_hover_style', 'Filter hover style')}
                    help={tr('cfg_groups_hover_style_help', 'Visual emphasis when a visitor hovers over a filter option')}
                    control={
                        <TextField select size="small"
                            value={config.layout.hoverStyle}
                            onChange={(e) => setLayout({ hoverStyle: e.target.value })}
                            sx={{ minWidth: 200 }}
                        >
                            <MenuItem value="bold">{tr('cfg_groups_hover_bold',      'Bold')}</MenuItem>
                            <MenuItem value="underline">{tr('cfg_groups_hover_underline', 'Underline')}</MenuItem>
                            <MenuItem value="color">{tr('cfg_groups_hover_color',     'Colored')}</MenuItem>
                        </TextField>
                    }
                />
            </ConfigCard>

            {/* Filters & their order + Settings per active filter */}
            <ConfigCard
                anchor="cfg-card-groups-order"
                title={tr('cfg_groups_order_title', 'Filters & their order')}
                description={tr('cfg_groups_order_desc', 'Pick which filters appear on the explore page and in what order. Each active filter gets its own settings below.')}
                theme={theme}
            >
                {/* How values inside the filters are sorted (ORDER_SIDE_FILTER_BY).
                    Option gating mirrors the legacy admin: "match header" needs
                    the tree filter style, "most items" excludes it. */}
                <PdiRow
                    label={tr('cfg_groups_order_by', 'Sort filters by')}
                    help={tr('cfg_groups_order_by_help', 'How the values inside the filters are ordered')}
                    control={
                        <TextField select size="small"
                            value={config.sortFiltersBy || 'NAME'}
                            onChange={(e) => setConfig(p => ({ ...p, sortFiltersBy: e.target.value }))}
                            sx={{ minWidth: 280 }}
                        >
                            <MenuItem value="NAME">{tr('cfg_groups_order_by_name', 'Name')}</MenuItem>
                            <MenuItem value="NAME_BUT_MATCH_HEADER" disabled={String(filterStyle).toLowerCase() !== 'tree'}>{tr('cfg_groups_order_by_match_header', 'Name, but match the menu order (tree style only)')}</MenuItem>
                            <MenuItem value="MOST_ITEMS" disabled={String(filterStyle).toLowerCase() === 'tree'}>{tr('cfg_groups_order_by_items', 'Most items first (not with tree style)')}</MenuItem>
                        </TextField>
                    }
                />
                <Box>
                    <SubLabel>{tr('cfg_groups_active_label', 'Active filters (in order, top to bottom)')}</SubLabel>
                    <SortableActiveFilters
                        items={config.activeFilters}
                        onReorder={setActiveFilters}
                        onRemove={(k) => setActiveFilters(config.activeFilters.filter(x => x !== k))}
                        all={ALL_FILTERS}
                        tr={tr}
                    />
                    <Box sx={{ mt: 1 }}>
                        <SubLabel>{tr('cfg_groups_available_label', 'Available (click to add)')}</SubLabel>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {ALL_FILTERS.filter(f => !config.activeFilters.includes(f.key)).map(f => (
                                <AvailableChip
                                    key={f.key}
                                    label={labelOf(f)}
                                    onAdd={() => setActiveFilters([...config.activeFilters, f.key])}
                                />
                            ))}
                        </Box>
                    </Box>
                </Box>

                <Box>
                    <SubLabel>{tr('cfg_groups_per_filter', 'Settings per active filter')}</SubLabel>
                    <Stack spacing={1}>
                        {config.activeFilters.map(k => {
                            const meta = ALL_FILTERS.find(f => f.key === k);
                            if (!meta) return null;
                            const s = config.filterSettings[k] || {};
                            return (
                                <Box key={k} sx={{ p: 1.25, borderRadius: 1.5, backgroundColor: 'rgba(0,0,0,0.03)', border: '1px solid', borderColor: 'divider' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                                        <Box sx={{ width: 24, height: 24, borderRadius: 0.75, backgroundColor: 'rgba(25,118,210,0.08)', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{meta.icon}</Box>
                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{labelOf(meta)}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, alignItems: 'center' }}>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                                {tr('cfg_groups_items_before_more', 'Items shown before "show more"')}
                                            </Typography>
                                            <TextField
                                                size="small" type="number"
                                                value={s.itemsBeforeMore ?? ''}
                                                onChange={(e) => setFilterSetting(k, { itemsBeforeMore: e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value) || 0) })}
                                                placeholder={k === 'price' ? tr('cfg_groups_no_limit', 'No limit') : ''}
                                                InputProps={{ endAdornment: <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.8rem', ml: 1 }}>{tr('cfg_groups_items', 'items')}</Box> }}
                                                sx={{ width: 160, backgroundColor: '#fff' }}
                                            />
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Switch color="success" size="small" checked={!!s.expandedByDefault} onChange={(e) => setFilterSetting(k, { expandedByDefault: e.target.checked })} />
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {tr('cfg_groups_expanded', 'Expanded by default')}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>
            </ConfigCard>

            {/* Mobile filter hierarchy */}
            <ConfigCard
                anchor="cfg-card-groups-mobile"
                title={tr('cfg_groups_mobile_title', 'Mobile filter hierarchy')}
                description={tr('cfg_groups_mobile_desc', 'On mobile, filters can be organized as a tree. Pick the category levels visitors drill into, in order.')}
                theme={theme}
            >
                <ListChipPicker
                    orderedLabel={tr('cfg_groups_hierarchy_label', 'Hierarchy levels (top to bottom)')}
                    available={ALL_HIERARCHY}
                    used={config.mobileHierarchy}
                    setUsed={setMobileHierarchy}
                    tr={tr}
                />
            </ConfigCard>

            {/* Sort options */}
            <ConfigCard
                anchor="cfg-card-groups-sort"
                title={tr('cfg_groups_sort_title', 'Sort options visitors can choose')}
                description={tr('cfg_groups_sort_desc', 'Pick which sort options appear in the dropdown for visitors, and in what order.')}
                theme={theme}
            >
                <ListChipPicker
                    orderedLabel={tr('cfg_groups_sort_available_label', 'Available sort options (top to bottom)')}
                    available={ALL_SORTS}
                    used={config.sortOptions}
                    setUsed={setSortOptions}
                    tr={tr}
                />
            </ConfigCard>

            {/* Default sort rule */}
            <ConfigCard
                anchor="cfg-card-groups-default"
                title={tr('cfg_groups_default_sort_title', 'Default sort rule')}
                description={tr('cfg_groups_default_sort_desc', 'When a visitor uses the "Recommended" sort, products are ordered by these rules. Drag to reorder priorities — the first rule wins, ties are broken by the next.')}
                theme={theme}
            >
                <Stack spacing={0.75}>
                    {config.defaultSortRule.map((rule, idx) => (
                        <Box key={rule.id} sx={{
                            display: 'grid',
                            gridTemplateColumns: '24px 32px 1fr auto auto',
                            alignItems: 'center', gap: 1,
                            backgroundColor: '#fff',
                            border: '1px solid', borderColor: 'divider',
                            borderRadius: 1, px: 1.25, py: 1,
                        }}>
                            <Box sx={{ color: 'text.disabled', fontSize: 14, fontFamily: 'monospace' }}>::</Box>
                            <Box sx={{
                                width: 26, height: 26, borderRadius: '50%',
                                backgroundColor: 'rgba(0,0,0,0.04)', color: 'text.secondary',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontWeight: 700, fontSize: '0.78rem',
                            }}>{idx + 1}</Box>
                            <TextField
                                select size="small"
                                value={rule.field}
                                onChange={(e) => setSortRules(config.defaultSortRule.map(r => r.id === rule.id ? { ...r, field: e.target.value } : r))}
                                sx={{ minWidth: 0 }}
                            >
                                {SORT_RULE_FIELDS.map(o => <MenuItem key={o.v} value={o.v}>{tr(o.i18nKey, o.l)}</MenuItem>)}
                            </TextField>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <DirToggle active={rule.direction === 'asc'}  onClick={() => setSortRules(config.defaultSortRule.map(r => r.id === rule.id ? { ...r, direction: 'asc'  } : r))}>↑ {tr('cfg_groups_rule_asc', 'Asc')}</DirToggle>
                                <DirToggle active={rule.direction === 'desc'} onClick={() => setSortRules(config.defaultSortRule.map(r => r.id === rule.id ? { ...r, direction: 'desc' } : r))}>↓ {tr('cfg_groups_rule_desc', 'Desc')}</DirToggle>
                            </Box>
                            <IconButton size="small" onClick={() => setSortRules(config.defaultSortRule.filter(r => r.id !== rule.id))} sx={{ color: 'text.disabled' }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    ))}
                    <ButtonBase
                        onClick={() => setSortRules([...config.defaultSortRule, { id: `r_${Date.now()}`, field: 'id', direction: 'asc' }])}
                        sx={{
                            py: 1, borderRadius: 1,
                            border: '1.5px dashed', borderColor: 'divider',
                            color: 'text.secondary', gap: 0.5,
                            '&:hover': { borderColor: 'primary.main', color: 'primary.main', backgroundColor: 'rgba(25,118,210,0.04)' },
                        }}
                    >
                        <AddIcon fontSize="small" />
                        <Typography variant="body2">{tr('cfg_groups_add_rule', 'Add another rule')}</Typography>
                    </ButtonBase>
                </Stack>
            </ConfigCard>
        </Box>
    );
};
GroupsFilterModal.propTypes = {
    value:           PropTypes.object,
    onChange:        PropTypes.func,
    shopFieldLabels: PropTypes.object,
    filterStyle:     PropTypes.string,
    theme:           PropTypes.string,
    t:               PropTypes.func,
};

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────

// Pair (or larger) of mutually-exclusive visual tiles. Selected tile is
// blue-tinted, unselected is white-outlined. Used for "Where filters",
// "Size style", "Color style".
const TilePair = ({ options, value, onChange }) => (
    <Box sx={{ display: 'flex', gap: 1 }}>
        {options.map(o => {
            const sel = value === o.v;
            return (
                <ButtonBase
                    key={o.v}
                    onClick={() => onChange(o.v)}
                    sx={{
                        flexDirection: 'column', alignItems: 'center', gap: 0.5,
                        p: 1.25, minWidth: 110,
                        borderRadius: 1.5,
                        border: '1.5px solid',
                        borderColor: sel ? 'primary.main' : 'divider',
                        backgroundColor: sel ? 'rgba(25,118,210,0.08)' : '#fff',
                        color: sel ? 'primary.main' : 'text.secondary',
                    }}
                >
                    <Box sx={{
                        width: 36, height: 28, borderRadius: 0.75,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>{o.icon}</Box>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>{o.label}</Typography>
                </ButtonBase>
            );
        })}
    </Box>
);
TilePair.propTypes = { options: PropTypes.array, value: PropTypes.string, onChange: PropTypes.func };

// Vertical chip-list with × remove + AVAILABLE row. Used for hierarchy
// levels and sort options where order matters.
const ListChipPicker = ({ orderedLabel, available, used, setUsed, tr }) => (
    <Box>
        <SubLabel>{orderedLabel}</SubLabel>
        <ChipsBox sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
            {used.map((k, idx) => {
                const meta = available.find(f => f.key === k);
                if (!meta) return null;
                return (
                    <Box key={k} sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        backgroundColor: 'rgba(25,118,210,0.06)',
                        border: '1px solid', borderColor: 'primary.light',
                        borderRadius: 999, px: 1.25, py: 0.5,
                    }}>
                        <Box sx={{ color: 'text.disabled', fontSize: 14, fontFamily: 'monospace' }}>::</Box>
                        <Box sx={{
                            width: 22, height: 22, borderRadius: '50%',
                            backgroundColor: 'rgba(25,118,210,0.12)', color: 'primary.main',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontWeight: 700, fontSize: '0.75rem',
                        }}>{idx + 1}</Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', flex: 1 }}>
                            {meta.label ?? tr(meta.i18nKey, meta.fallback)}
                        </Typography>
                        <IconButton size="small" onClick={() => setUsed(used.filter(x => x !== k))} sx={{ color: 'text.disabled' }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                );
            })}
        </ChipsBox>
        <Box sx={{ mt: 1 }}>
            <SubLabel>{tr('cfg_groups_available_label', 'Available (click to add)')}</SubLabel>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {available.filter(f => !used.includes(f.key)).map(f => (
                    <AvailableChip
                        key={f.key}
                        label={f.label ?? tr(f.i18nKey, f.fallback)}
                        onAdd={() => setUsed([...used, f.key])}
                    />
                ))}
            </Box>
        </Box>
    </Box>
);
ListChipPicker.propTypes = { orderedLabel: PropTypes.node, available: PropTypes.array, used: PropTypes.array, setUsed: PropTypes.func, tr: PropTypes.func };

// Drag-and-drop wrapper for the Active filters list — wraps the rows in a
// DndContext + SortableContext and reorders via arrayMove on drop. Each row
// is a SortableFilterRow that wires up its own sortable handle.
const SortableActiveFilters = ({ items, onReorder, onRemove, all, tr }) => {
    const sensors = useSensors(
        // PointerSensor with a small activation distance so the × remove
        // button still works as a click — drag only kicks in after a few px.
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    );
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIdx = items.indexOf(active.id);
        const newIdx = items.indexOf(over.id);
        if (oldIdx < 0 || newIdx < 0) return;
        onReorder(arrayMove(items, oldIdx, newIdx));
    };
    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={items} strategy={verticalListSortingStrategy}>
                <ChipsBox sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
                    {items.map((k, idx) => {
                        const meta = all.find(f => f.key === k);
                        if (!meta) return null;
                        return (
                            <SortableFilterRow
                                key={k}
                                id={k}
                                idx={idx}
                                label={meta.label ?? tr(meta.i18nKey, meta.fallback)}
                                onRemove={() => onRemove(k)}
                            />
                        );
                    })}
                </ChipsBox>
            </SortableContext>
        </DndContext>
    );
};
SortableActiveFilters.propTypes = { items: PropTypes.array, onReorder: PropTypes.func, onRemove: PropTypes.func, all: PropTypes.array, tr: PropTypes.func };

// One draggable row in the Active filters list. The drag handle (`::`) is the
// only listener target so the row stays clickable for the × remove button.
const SortableFilterRow = ({ id, idx, label, onRemove }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex:  isDragging ? 1   : 0,
    };
    return (
        <Box ref={setNodeRef} style={style} sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            backgroundColor: 'rgba(25,118,210,0.06)',
            border: '1px solid', borderColor: 'primary.light',
            borderRadius: 999, px: 1.25, py: 0.5,
            width: '100%',
            // Keep the row above the next sibling's border while dragging so
            // the visual "lifted" effect reads cleanly.
            boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,0.15)' : 'none',
        }}>
            {/* Drag handle — clickable on this element only via attributes/listeners. */}
            <Box
                {...attributes}
                {...listeners}
                sx={{
                    color: 'text.disabled',
                    fontSize: 14,
                    fontFamily: 'monospace',
                    cursor: 'grab',
                    px: 0.5,
                    userSelect: 'none',
                    '&:active': { cursor: 'grabbing' },
                }}
            >::</Box>
            <Box sx={{
                width: 22, height: 22, borderRadius: '50%',
                backgroundColor: 'rgba(25,118,210,0.12)', color: 'primary.main',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: '0.75rem',
            }}>{idx + 1}</Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', flex: 1 }}>
                {label}
            </Typography>
            <IconButton size="small" onClick={onRemove} sx={{ color: 'text.disabled' }}>
                <CloseIcon fontSize="small" />
            </IconButton>
        </Box>
    );
};
SortableFilterRow.propTypes = { id: PropTypes.string, idx: PropTypes.number, label: PropTypes.node, onRemove: PropTypes.func };

// Asc / Desc pill toggle used in the default sort rule rows.
const DirToggle = ({ active, onClick, children }) => (
    <ButtonBase
        onClick={onClick}
        sx={{
            px: 1.25, py: 0.5, borderRadius: 0.75,
            border: '1px solid',
            borderColor: active ? 'primary.main' : 'divider',
            backgroundColor: active ? 'rgba(25,118,210,0.08)' : '#fff',
            color: active ? 'primary.main' : 'text.secondary',
            fontSize: '0.75rem', fontWeight: 600,
        }}
    >{children}</ButtonBase>
);
DirToggle.propTypes = { active: PropTypes.bool, onClick: PropTypes.func, children: PropTypes.node };

export default GroupsFilterModal;
